import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, Phone } from "lucide-react";

const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Privacy Policy
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">
              Last updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>

            <div className="prose prose-slate max-w-none">
              <h2>1. Introduction</h2>
              <p>
                Ecommerce Brokers, operated by Bonza Business & Franchise Sales ("we", "us", or "our"), 
                is committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website 
                ecommercebrokers.com.au and use our services.
              </p>
              <p>
                We comply with the Australian Privacy Principles (APPs) contained in the Privacy Act 
                1988 (Cth) and any applicable state or territory privacy legislation.
              </p>

              <h2>2. Information We Collect</h2>
              <h3>2.1 Personal Information</h3>
              <p>We may collect the following personal information:</p>
              <ul>
                <li>Name and contact details (email address, phone number)</li>
                <li>Business information (business name, website URL, business type)</li>
                <li>Financial information provided for business valuation purposes</li>
                <li>Communication preferences</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3>2.2 Automatically Collected Information</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul>
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Other usage data through cookies and similar technologies</li>
              </ul>

              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide business valuation services and guidance</li>
                <li>Respond to your enquiries and requests</li>
                <li>Connect you with appropriate business brokers</li>
                <li>Send you relevant information about our services (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>

              <h2>4. Disclosure of Your Information</h2>
              <p>We may share your information with:</p>
              <ul>
                <li>Bonza Business & Franchise Sales and affiliated business brokers</li>
                <li>Service providers who assist in operating our website and services</li>
                <li>Professional advisers (lawyers, accountants) as required</li>
                <li>Government authorities when required by law</li>
              </ul>
              <p>
                We will not sell, rent, or lease your personal information to third parties 
                for their marketing purposes without your explicit consent.
              </p>

              <h2>5. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your 
                personal information against unauthorised access, alteration, disclosure, or 
                destruction. However, no method of transmission over the Internet or electronic 
                storage is 100% secure.
              </p>

              <h2>6. Cookies and Tracking Technologies</h2>
              <p>
                Our website uses cookies and similar tracking technologies to enhance your 
                experience and collect usage data. We use Google Analytics to understand how 
                visitors interact with our website. You can control cookie preferences through 
                your browser settings.
              </p>

              <h2>7. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible 
                for the privacy practices of these external sites. We encourage you to review 
                their privacy policies.
              </p>

              <h2>8. Your Rights</h2>
              <p>Under Australian privacy law, you have the right to:</p>
              <ul>
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Lodge a complaint with the Office of the Australian Information Commissioner</li>
              </ul>

              <h2>9. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfil 
                the purposes for which it was collected, or as required by law. Valuation 
                data may be retained for business records and to provide ongoing services.
              </p>

              <h2>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of 
                any material changes by posting the new policy on this page with an updated 
                revision date.
              </p>

              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or wish to exercise your 
                privacy rights, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 not-prose">
                <p className="font-semibold text-slate-900 mb-2">Bonza Business & Franchise Sales</p>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:sales@bonzabfs.com.au" className="text-blue-600 hover:underline">
                      sales@bonzabfs.com.au
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href="tel:1300266922" className="text-blue-600 hover:underline">
                      1300 266 922
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-8 brightness-0 invert" />
              <span className="text-sm">Powered by Bonza Business & Franchise Sales</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-white">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
